
import React from 'react';
import { Task, Contact, User as UserType } from '../types';
import { Check, Trash2, CheckCircle, Clock, UserCheck } from 'lucide-react';

interface TaskListProps {
  tasks: Task[];
  contacts: Contact[];
  users: UserType[];
  onToggleTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
  onSelectContact: (contact: Contact) => void;
}

const PriorityBadge = ({ priority, isCompleted }: { priority: string, isCompleted: boolean }) => {
  const colors = {
    High: 'bg-rose-100 text-rose-700 border-rose-200',
    Medium: 'bg-amber-100 text-amber-700 border-amber-200',
    Low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest border ${isCompleted ? 'bg-slate-100 text-slate-400 border-slate-200' : colors[priority as keyof typeof colors]}`}>
      {priority}
    </span>
  );
};

interface TaskRowProps {
  task: Task;
  assignedUser?: UserType;
  onToggleTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
}

const TaskRow: React.FC<TaskRowProps> = ({ 
  task, 
  assignedUser,
  onToggleTask, 
  onDeleteTask 
}) => {
  const isCompleted = task.status === 'Completed';
  
  return (
    <div className={`flex items-center p-6 bg-white border rounded-[32px] shadow-sm group transition-all duration-300 ${isCompleted ? 'opacity-60 grayscale border-emerald-100 bg-emerald-50/10' : 'border-slate-200 hover:border-indigo-400 hover:shadow-xl'}`}>
      <button
        onClick={() => onToggleTask(task.id)}
        className={`w-10 h-10 rounded-2xl border-2 flex items-center justify-center transition-all shrink-0 mr-6 ${
          isCompleted 
            ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg shadow-emerald-100' 
            : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 text-transparent hover:text-indigo-400'
        }`}
      >
        {isCompleted ? <CheckCircle size={24} /> : <Check size={20} strokeWidth={3} />}
      </button>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <p className={`text-sm font-black text-slate-800 truncate uppercase tracking-tight ${isCompleted ? 'line-through text-slate-400' : ''}`}>
            {task.title}
          </p>
          <PriorityBadge priority={task.priority} isCompleted={isCompleted} />
        </div>
        
        <div className="flex items-center gap-6 mt-2 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-slate-400">
            <Clock size={12} className="text-slate-300" />
            Due: <span className={isCompleted ? 'text-slate-300' : 'text-slate-900'}>{task.dueDate}</span>
          </div>

          <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 border border-slate-100 rounded-xl" title={`Assigned to ${assignedUser?.fullName || 'Staff'}`}>
              <div className="w-5 h-5 rounded-lg overflow-hidden bg-indigo-100 border border-white flex items-center justify-center text-indigo-700">
                 {assignedUser?.avatarUrl ? <img src={assignedUser.avatarUrl} alt="" className="w-full h-full object-cover" /> : <UserCheck size={10} />}
              </div>
              <span className="text-[10px] font-black uppercase text-slate-600 truncate max-w-[150px]">
                {assignedUser?.fullName || 'Unknown Personnel'}
              </span>
          </div>
        </div>

        {task.description && (
          <p className="mt-2 text-xs text-slate-400 leading-relaxed line-clamp-1 font-medium italic">{task.description}</p>
        )}
      </div>

      <div className="flex items-center gap-2 ml-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          onClick={() => onDeleteTask(task.id)}
          className="p-3 text-slate-300 hover:text-rose-500 rounded-2xl hover:bg-rose-50 transition-all"
          title="Remove from Ledger"
        >
          <Trash2 size={20} />
        </button>
      </div>
    </div>
  );
};

const TaskList: React.FC<TaskListProps> = ({ 
  tasks, 
  users,
  onToggleTask, 
  onDeleteTask 
}) => {
  const getAssignedUser = (id?: string) => users.find(u => u.id === id);

  return (
    <div className="max-w-5xl mx-auto pb-12">
      <div className="space-y-4">
        {tasks.map(task => (
          <TaskRow 
            key={task.id} 
            assignedUser={getAssignedUser(task.assigneeId)}
            onToggleTask={onToggleTask}
            onDeleteTask={onDeleteTask}
            task={task}
          />
        ))}
        {tasks.length === 0 && (
          <div className="bg-white border-2 border-dashed border-slate-100 rounded-[40px] py-32 flex flex-col items-center justify-center text-slate-300">
            <CheckCircle size={48} className="opacity-10 mb-4" />
            <p className="font-black text-[10px] uppercase tracking-[0.4em] text-slate-400">Operational Ledger Empty</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskList;
